import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import html2pdf from "html2pdf.js";
import { 
  Star, Download, Copy, Share2, ChevronDown, MapPin, Building2, Mail, User, 
  Loader2, AlertCircle, Check, ExternalLink, Search, Palette, Printer, Sparkles,
  MousePointerClick, FileText, QrCode
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { HEADER, HERO, DESIGN_STUDIO, TESTIMONIALS, FAQ, FOOTER } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}api`;

const PRESET_MESSAGES = [
  { id: "preset1", text: "Tack för ditt besök! Scanna för att lämna en recension." },
  { id: "preset2", text: "Smakade det bra? Berätta vad du tyckte!" },
  { id: "preset3", text: "Hjälp oss att växa! Dela din upplevelse på Google." },
  { id: "preset4", text: "Vi uppskattar din feedback! Scanna och betygsätt oss." },
  { id: "custom", text: "Egen text..." },
];

const TESTIMONIALS_DATA = [
  {
    id: 1,
    quote: "Det var väldigt smidigt att få fram koden. Vi skrev ut skylten och ställde den vid kassan samma dag. Det har gjort det betydligt lättare att påminna nöjda gäster om att lämna en recension innan de går.",
    name: "Johan Eriksson",
    role: "Restaurangägare i Malmö",
    avatar: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
    business: "Bella Pizza",
  },
  {
    id: 2,
    quote: "Enkelt och utan krångel. Jag behövde inte skapa något konto eller fylla i massa uppgifter, utan kunde ladda ner filen direkt. Nu har vi koden vid varje spegel på salongen.",
    name: "Karin Lindström",
    role: "Frisör i Helsingborg",
    avatar: "https://images.pexels.com/photos/3776932/pexels-photo-3776932.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
    business: "Karins Klippstuga",
  },
  {
    id: 3,
    quote: "Många av våra kunder vill gärna lämna ett bra betyg men orkar inte söka upp oss manuellt på Google. Med QR-koden på kvittot eller disken går det på ett par sekunder för dem.",
    name: "Magnus Johansson",
    role: "Verkstadsägare i Höör",
    avatar: "https://images.pexels.com/photos/8422729/pexels-photo-8422729.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
    business: "Magnus Bil & Motor",
  },
];

const FAQ_DATA = [
  {
    id: "faq1",
    question: "Hur får man fler Google-recensioner?",
    answer: `Det enklaste och mest effektiva sättet att få fler Google-recensioner är att göra processen så smidig som möjligt för dina kunder. Här är våra bästa tips:

**1. Använd en QR-kod**
Med en QR-kod som leder direkt till din Google-recensions-sida behöver kunden bara scanna koden med sin telefon – inga krångliga sökningar eller klick. Placera QR-koden synligt i din butik, på bordet i restaurangen, vid kassan, på kvittot eller på visitkortet.

**2. Be om recensioner vid rätt tillfälle**
Det bästa tillfället att be om en recension är precis efter en positiv kundupplevelse. Om kunden verkar nöjd, passa på att säga: "Vi skulle verkligen uppskatta om du kunde dela din upplevelse på Google!"

**3. Gör det personligt**
En personlig förfrågan är mycket mer effektiv än ett generiskt meddelande. Använd kundens namn och referera till den specifika tjänsten eller produkten de köpte.

**4. Följ upp via e-post eller SMS**
Skicka ett vänligt uppföljningsmeddelande med en direkt länk till din Google-recensions-sida. Ju färre steg, desto fler recensioner!

Generera din egen QR-kod för recensioner helt gratis med vårt verktyg här ovan – det tar mindre än 60 sekunder.`,
  },
  {
    id: "faq2",
    question: "Varför syns inte mitt företag på Google Maps?",
    answer: `Om ditt företag inte syns på Google Maps eller rankar dåligt i lokala sökresultat kan det bero på flera faktorer. Här är de vanligaste orsakerna och hur du åtgärdar dem:

**1. Din Google Business Profile är inte verifierad**
Google visar endast verifierade företag i sökresultaten. Se till att du har skapat en Google Business Profile (tidigare Google My Business) och genomgått verifieringsprocessen via brev, telefon eller e-post.

**2. Ofullständig profil**
Google prioriterar företag med kompletta profiler. Se till att du har fyllt i:
• Exakt företagsnamn och adress
• Telefonnummer och öppettider
• Kategori och underkategorier
• Beskrivning av verksamheten
• Högkvalitativa bilder

**3. För få eller dåliga recensioner**
Recensioner är en av de viktigaste rankingfaktorerna för lokal SEO. Företag med fler positiva recensioner rankar högre än konkurrenter med färre omdömen.

**4. Inkonsekvent NAP-information**
NAP står för Name, Address, Phone. Om din företagsinformation skiljer sig mellan olika webbplatser (din hemsida, Eniro, Hitta.se, etc.) kan Google bli förvirrad och välja att inte visa ditt företag.

**5. Låg aktivitet**
Google belönar aktiva företag. Publicera regelbundna inlägg, svara på recensioner och uppdatera din information för att visa att du är aktiv.

Börja med att samla in fler recensioner – det är ofta den snabbaste vägen till bättre synlighet!`,
  },
  {
    id: "faq3",
    question: "Hur påverkar Google-recensioner min ranking?",
    answer: `Google-recensioner är en av de allra viktigaste rankingfaktorerna för lokal SEO (sökmotoroptimering). Enligt branschstudier utgör recensioner cirka 15-17% av hur Google rangordnar lokala företag. Här är hur din recensions-ranking påverkar din synlighet:

**Direkta rankingfaktorer:**
• **Antal recensioner** – Fler recensioner signalerar till Google att ditt företag är populärt och pålitligt
• **Genomsnittligt betyg** – Företag med 4+ stjärnor rankar generellt högre
• **Frekvens** – Regelbundna nya recensioner visar att företaget är aktivt
• **Sökord i recensioner** – När kunder nämner specifika tjänster eller produkter hjälper det Google förstå vad du erbjuder

**Indirekta fördelar:**
• **Högre klickfrekvens (CTR)** – Företag med många positiva recensioner får fler klick i sökresultaten
• **Förtroende och konvertering** – 93% av konsumenter läser recensioner innan de väljer ett lokalt företag
• **Längre besökstider** – Användare spenderar mer tid på att läsa recensioner, vilket signalerar kvalitet till Google

**Konkreta siffror:**
• Företag med 4.0-4.5 stjärnor får i genomsnitt 28% fler klick än de med 3.0-3.5 stjärnor
• 50+ recensioner kan öka din synlighet med upp till 40% i lokala sökresultat
• Att svara på recensioner kan förbättra din ranking med 5-10%

**Tips för bättre ranking:**
1. Samla in nya recensioner kontinuerligt med en QR-kod
2. Svara alltid på recensioner – både positiva och negativa
3. Uppmuntra kunder att nämna specifika tjänster i sina recensioner
4. Ha alltid uppdaterad information i din Google Business Profile

Starta din recensionsresa idag med ett professionellt flygblad som du kan ladda ner gratis här ovan!`,
  },
  {
    id: "faq4",
    question: "Kan jag välja en egen mall för mitt QR-kod-flygblad?",
    answer: `Ja! När du genererar din QR-kod får du välja mellan flera färdiga mallar för ditt flygblad, och du kan anpassa både storlek och meddelande efter din verksamhet.

**Vad ingår gratis:**
• Obegränsad sökning efter företag
• Tre professionella flygbladsmallar för QR-kod-recensioner
• Anpassningsbara meddelanden på svenska
• Högupplöst PDF-nedladdning redo för tryck, i A4 eller A5
• QR-kod som leder direkt till din Google-recensions-sida

**Varför erbjuder vi detta gratis?**
Vi på Mellan Utveckling tror på att stärka det lokala näringslivet i Skåne. Genom att hjälpa företag samla in fler recensioner bidrar vi till en starkare lokal ekonomi. Vi erbjuder också professionella tjänster inom webbutveckling och lokal SEO för företag som vill ta nästa steg.

**Ingen registrering krävs**
Du behöver inte skapa något konto eller lämna ut personuppgifter för att generera din QR-kod. Sök efter ditt företag, anpassa flygbladet och ladda ner direkt!`,
  },
  {
    id: "faq5",
    question: "Vilka företag kan använda QR-kod-generatorn för recensioner?",
    answer: `Vårt verktyg är designat för alla typer av lokala företag i Sverige som har en Google Business Profile. Här är några exempel på verksamheter som har stor nytta av ett QR-kod-flygblad för recensioner:

**Restauranger och caféer**
Placera QR-koden på borden, vid kassan eller på notan. Kunder kan enkelt lämna en recension medan upplevelsen fortfarande är färsk i minnet.

**Frisörer och skönhetssalonger**
Sätt upp flygbladet vid spegeln eller receptionen. Nöjda kunder kan scanna koden direkt efter behandlingen.

**Verkstäder och bilservice**
Inkludera QR-koden på fakturan eller i väntrummet. Bilägare uppskattar enkelheten att kunna recensera på en gång.

**Butiker och detaljhandel**
Placera vid kassan eller i skyltfönstret. Stamkunder kan snabbt visa sin uppskattning.

**Tandläkare och vårdmottagningar**
Ha flygbladet i receptionen. Patienter kan lämna omdömen medan de väntar eller efter sitt besök.

**Hantverkare och servicetekniker**
Ge kunden ett flygblad efter avslutat jobb. QR-koden gör det enkelt att recensera direkt från mobilen.

**Gym och träningsanläggningar**
Sätt upp vid receptionen eller i omklädningsrummet. Motiverade medlemmar delar gärna sina framgångar!

Oavsett bransch – om du har ett fysiskt företag och vill ha fler Google-recensioner är QR-kod-generatorn för dig.`,
  },
];

const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-slate-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <a href="/" data-testid={HEADER.logo} className="flex items-center gap-2 font-bold text-xl text-slate-900">
          <img 
            src="https://mellanutveckling.se/images/logo.png" 
            alt="Mellan Utveckling Logo" 
            className="h-8 w-auto object-contain" 
          />
        </a>
        <a
          href="https://mellanutveckling.se"
          target="_blank"
          rel="noopener noreferrer"
          data-testid={HEADER.ctaButton}
        >
          <Button variant="outline" className="hidden sm:flex items-center gap-2 border-slate-300 hover:bg-slate-50">
            Våra tjänster
            <ExternalLink className="w-4 h-4" />
          </Button>
        </a>
      </div>
    </div>
  </header>
);

const HeroSection = ({ onBusinessSelected, selectedBusiness, searchQuery, setSearchQuery }) => {
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchPredictions = async () => {
      if (searchQuery.length < 2) {
        setPredictions([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/v1/places/autocomplete`, {
          params: { query: searchQuery },
        });
        setPredictions(response.data.predictions || []);
        setShowDropdown(true);
      } catch (error) {
        console.error("Autocomplete error:", error);
      }
    };

    const debounce = setTimeout(fetchPredictions, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectBusiness = (prediction) => {
    onBusinessSelected(prediction);
    setSearchQuery(prediction.description);
    setShowDropdown(false);
  };

  return (
    <section className="pt-28 pb-12 px-6 lg:px-8 hero-gradient">
      <div className="max-w-4xl mx-auto text-center">
        <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium text-blue-700 bg-blue-50 rounded-full">
          Gratis QR-kod för recensioner – för lokala företag i Skåne
        </span>
        
        <h1 data-testid={HERO.title} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
          Skapa QR-kod för Google-recensioner – helt gratis
        </h1>
        
        <p data-testid={HERO.subtitle} className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
          Generera din QR-kod och ett professionellt flygblad (PDF) för ditt företag i Skåne på under 1 minut. 
          Öka din synlighet på Google Maps och få fler kunder att hitta dig.
        </p>

        <Card className="max-w-2xl mx-auto border-slate-200 shadow-xl">
          <CardContent className="p-6">
            <div className="relative" ref={searchRef}>
              <Label htmlFor="business-search" className="text-left block mb-2 text-sm font-medium text-slate-700">
                Sök efter ditt företag på Google Maps
              </Label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="business-search"
                  data-testid={HERO.searchInput}
                  type="text"
                  placeholder="T.ex. Bella Pizza Eslöv, Karins Salong Landskrona..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (selectedBusiness) onBusinessSelected(null);
                  }}
                  className="pl-12 h-14 text-lg border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                />
                {selectedBusiness && (
                  <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
              </div>

              {showDropdown && predictions.length > 0 && (
                <div data-testid={HERO.searchDropdown} className="autocomplete-dropdown mt-2">
                  {predictions.map((prediction) => (
                    <div
                      key={prediction.place_id}
                      className="autocomplete-item flex items-center gap-3"
                      onClick={() => handleSelectBusiness(prediction)}
                    >
                      <MapPin className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-slate-900">{prediction.structured_formatting.main_text}</p>
                        <p className="text-sm text-slate-500">{prediction.structured_formatting.secondary_text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!selectedBusiness && (
              <p className="text-sm text-slate-500 mt-4 text-center">
                💡 Testa: sök efter pizza, salong, bilverkstad, café eller gym
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

const DesignStudio = ({ selectedBusiness, onSubmitLead }) => {
  const [selectedSize, setSelectedSize] = useState("a4");
  const [selectedPreset, setSelectedPreset] = useState("preset1");
  const [customText, setCustomText] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [studioData, setStudioData] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [showErrorNotice, setShowErrorNotice] = useState(false);
  const flyerRef = useRef(null);

  const currentMessage = selectedPreset === "custom" 
    ? customText 
    : PRESET_MESSAGES.find(p => p.id === selectedPreset)?.text || "";

  const businessName = studioData?.businessName || selectedBusiness?.structured_formatting?.main_text || "Ditt Företag";
  const reviewUrl = studioData?.reviewUrl || "https://search.google.com/local/writereview?placeid=example";

  const sizeConfig = {
    a4: {
      aspectRatio: '1 / 1.414',
      format: 'a4',
      label: 'A4',
      qrSize: 140,
      logoSize: 'w-16 h-16',
      starSize: 'w-7 h-7',
      titleSize: 'text-2xl',
      businessSize: 'text-lg',
      messageSize: 'text-base',
      padding: '32px 28px 24px 28px',
      minHeight: '520px'
    },
    a5: {
      aspectRatio: '1 / 1.414',
      format: 'a5',
      label: 'A5',
      qrSize: 110,
      logoSize: 'w-12 h-12',
      starSize: 'w-5 h-5',
      titleSize: 'text-xl',
      businessSize: 'text-sm',
      messageSize: 'text-sm',
      padding: '24px 20px 16px 20px',
      minHeight: '380px'
    }
  };

  const config = sizeConfig[selectedSize];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Fyll i alla kontaktuppgifter");
      return;
    }

    setIsLoading(true);
    setShowErrorNotice(false);

    try {
      let recaptchaToken = null;
      if (typeof window.grecaptcha !== "undefined") {
        try {
          recaptchaToken = await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
              window.grecaptcha
                .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: "qr_lead" })
                .then(resolve)
                .catch(reject);
            });
          });
        } catch (captchaError) {
          console.warn("reCAPTCHA blockerades eller misslyckades", captchaError);
        }
      }

      const response = await axios.post(`${API}/v1/qr-lead`, {
        name: formData.name,
        email: formData.email,
        google_place_id: selectedBusiness.place_id,
        selected_template_id: selectedSize,
        custom_text: selectedPreset === "custom" ? customText : null,
        recaptcha_token: recaptchaToken,
      });

      if (response.data.success) {
        setStudioData({
          reviewUrl: response.data.review_url,
          businessName: response.data.business_name,
        });
        onSubmitLead(response.data);
        toast.success("QR-kod genererad! Din länk är redo för nedladdning.");
      }
    } catch (error) {
      console.error("Lead submission error:", error);
      if (error?.response?.status === 422 && error?.response?.data?.errors?.recaptcha_token) {
        toast.error("Vi kunde inte verifiera att du är en person. Ladda om sidan och försök igen.");
      } else {
        setShowErrorNotice(true);
        toast.error("Vi kunde inte hitta företaget automatiskt. Vi kontaktar dig!");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    const element = document.getElementById('flyer-preview-card');
    if (!element) {
      toast.error("Kunde inte hitta flygbladet");
      return;
    }

    const size = selectedSize; 
    
    const opt = {
      margin: 0,
      filename: `qr-flygblad-${size.toLowerCase()}-${businessName?.toLowerCase().replace(/\s+/g, '-') || 'recension'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2, 
        useCORS: true, 
        letterRendering: true,
        scrollY: -window.scrollY
      },
      jsPDF: { 
        unit: 'mm', 
        format: size.toLowerCase(), 
        orientation: 'portrait' 
      }
    };

    try {
      await html2pdf().set(opt).from(element).toContainer().toCanvas().toImg().toPdf().save();
      
      if(size === "a5") {
         toast.success(`PDF nedladdad! Viktigt: Välj "Verklig storlek" vid utskrift för rätt format.`);
      } else {
         toast.success(`PDF nedladdad i ${config.label}-format! Redo för utskrift.`);
      }
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Kunde inte generera PDF");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(reviewUrl);
      setIsCopied(true);
      toast.success("Länk kopierad till urklipp!");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast.error("Kunde inte kopiera länk");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Lämna en recension för ${businessName}`,
          text: currentMessage,
          url: reviewUrl,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <section data-testid={DESIGN_STUDIO.container} className="py-8 px-6 lg:px-8 bg-white">
      <div className="max-w-5xl mx-auto">
        {showErrorNotice && (
          <div data-testid={HERO.errorNotice} className="mb-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Hoppsan! Vi kunde inte hitta ditt företag automatiskt</h3>
                <p className="text-amber-800 text-sm">
                  Vi kunde inte hitta ditt företag på Google Maps just nu. Oroa dig inte – vi har tagit emot dina uppgifter 
                  och gör en manuell analys. Vi skickar din QR-kod personligen inom kort!
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Palette className="w-5 h-5 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-900">Anpassa din QR-kod-mall</h2>
          </div>
          <p className="text-slate-600">
            Välj storlek och meddelande. Förhandsvisningen uppdateras i realtid.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <Label className="text-sm font-semibold text-slate-800 mb-3 block">
              1. Välj storlek
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="size-a4"
                onClick={() => setSelectedSize("a4")}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  selectedSize === "a4" 
                    ? "border-blue-600 ring-2 ring-blue-100 bg-blue-50" 
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-8 h-11 rounded border-2 ${selectedSize === "a4" ? "border-blue-600 bg-blue-100" : "border-slate-300 bg-slate-50"}`} />
                  <span className="text-lg font-bold text-slate-900">A4</span>
                </div>
                <p className="text-xs text-slate-500">Standard för vägg/fönster</p>
              </button>
              <button
                data-testid="size-a5"
                onClick={() => setSelectedSize("a5")}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  selectedSize === "a5" 
                    ? "border-blue-600 ring-2 ring-blue-100 bg-blue-50" 
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-6 h-8 rounded border-2 ${selectedSize === "a5" ? "border-blue-600 bg-blue-100" : "border-slate-300 bg-slate-50"}`} />
                  <span className="text-lg font-bold text-slate-900">A5</span>
                </div>
                <p className="text-xs text-slate-500">Perfekt för bord/diskar</p>
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="preset-select" className="text-sm font-semibold text-slate-800 mb-3 block">
              2. Välj meddelande
            </Label>
            <Select value={selectedPreset} onValueChange={setSelectedPreset}>
              <SelectTrigger data-testid={DESIGN_STUDIO.presetDropdown} className="w-full border-slate-300 h-12">
                <SelectValue placeholder="Välj ett förformulerat meddelande" />
              </SelectTrigger>
              <SelectContent>
                {PRESET_MESSAGES.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedPreset === "custom" && (
              <div className="mt-3">
                <Textarea
                  data-testid={DESIGN_STUDIO.customTextInput}
                  placeholder="Skriv ditt eget meddelande..."
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value.slice(0, 100))}
                  className="border-slate-300 resize-none"
                  rows={2}
                />
                <p className="text-xs text-slate-500 mt-1">{customText.length}/100 tecken</p>
              </div>
            )}
          </div>
        </div>

        <div className="mb-8">
          <Label className="text-sm font-semibold text-slate-800 mb-4 block text-center flex items-center justify-center gap-2">
            <QrCode className="w-4 h-4 text-blue-600" />
            Förhandsvisning ({config.label})
          </Label>
          
          <div className="relative max-w-md mx-auto">
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-40 h-6 bg-slate-300/50 rounded-full blur-xl" />
            
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-28 h-2 bg-gradient-to-b from-slate-300 to-slate-400 rounded-full" />
            
            <div
              id="flyer-preview-card"
              ref={flyerRef}
              data-testid={DESIGN_STUDIO.livePreview}
              className="flyer-canvas bg-white flex flex-col items-center border border-slate-200 shadow-2xl mx-auto"
              style={{ 
                padding: config.padding,
                maxWidth: selectedSize === 'a4' ? '380px' : '280px',
                overflow: 'visible'
              }}
            >
              <h3 className={`${config.titleSize} font-bold text-slate-900 text-center mb-1 leading-tight`}>
                {businessName}
              </h3>
              
              <p className={`${config.businessSize} text-slate-500 text-center mb-4`}>
                Lämna en recension på Google
              </p>

              <div className="mb-2 flex justify-center">
                <svg 
                  viewBox="0 0 24 24" 
                  className={config.logoSize}
                  aria-label="Google"
                >
                  <path 
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" 
                    fill="#4285F4"
                  />
                  <path 
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" 
                    fill="#34A853"
                  />
                  <path 
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" 
                    fill="#FBBC05"
                  />
                  <path 
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" 
                    fill="#EA4335"
                  />
                </svg>
              </div>

              <div className="flex items-center justify-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <svg 
                    key={i} 
                    viewBox="0 0 24 24" 
                    className={config.starSize}
                  >
                    <path 
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      fill="#FBBF24"
                    />
                  </svg>
                ))}
              </div>

              <p className={`text-center ${config.messageSize} text-slate-700 mb-4 leading-relaxed px-2`}>
                {currentMessage || "Scanna QR-koden nedan för att lämna ett omdöme!"}
              </p>

              <div 
                data-testid={DESIGN_STUDIO.qrCode} 
                className="bg-white p-2 rounded-lg border border-slate-100 mb-4"
                style={{
                  width: config.qrSize + 16,
                  height: config.qrSize + 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <QRCodeSVG
                  value={reviewUrl}
                  size={config.qrSize}
                  level="H"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>

              <p className="text-center text-xs text-slate-400 tracking-wide mt-auto pt-2">
                mellanutveckling.se
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-8">
            👆 Detta är exakt hur ditt flygblad ser ut vid utskrift
          </p>
        </div>

        <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200 max-w-2xl mx-auto">
          <Label className="text-base font-semibold text-slate-800 mb-4 block text-center">
            3. Fyll i dina uppgifter för att aktivera nedladdning
          </Label>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid={HERO.nameInput}
                  type="text"
                  placeholder="Ditt namn"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 border-slate-300 bg-white h-12"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid={HERO.emailInput}
                  type="email"
                  placeholder="Din e-post"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 border-slate-300 bg-white h-12"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid={HERO.submitButton}
              disabled={isLoading}
              className="w-full h-14 text-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Genererar din QR-kod...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generera min QR-kod
                </>
              )}
            </Button>
          </form>
        </div>

        {studioData && (
          <div className="max-w-2xl mx-auto mt-6 space-y-3">
            <Button
              data-testid={DESIGN_STUDIO.downloadPdfButton}
              onClick={handleDownloadPdf}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Ladda ner PDF ({config.label} - Utskriftsvänlig)
            </Button>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid={DESIGN_STUDIO.copyLinkButton}
                variant="outline"
                onClick={handleCopyLink}
                className="h-12 border-slate-300"
              >
                {isCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    Kopierad!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Kopiera länk
                  </>
                )}
              </Button>
              <Button
                data-testid={DESIGN_STUDIO.shareLinkButton}
                variant="outline"
                onClick={handleShare}
                className="h-12 border-slate-300"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Dela länk
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      icon: Search,
      title: "1. Sök efter ditt företag",
      description: "Skriv in namnet på ditt företag i sökfältet. Vi hittar automatiskt din Google Business Profile och hämtar rätt recensionslänk.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: Palette,
      title: "2. Anpassa din QR-kod-mall",
      description: "Välj storlek (A4 eller A5) och skriv ett meddelande som passar din verksamhet. Se förhandsvisningen uppdateras i realtid.",
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: Printer,
      title: "3. Skriv ut och dela",
      description: "Ladda ner en högupplöst PDF redo för utskrift. Sätt upp flygbladet i din butik, på borden eller vid kassan – och se recensionerna rulla in!",
      color: "bg-green-100 text-green-600",
    },
  ];

  return (
    <section className="py-20 px-6 lg:px-8 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium text-blue-700 bg-blue-50 rounded-full">
            Enkelt & snabbt
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Hur fungerar det?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Tre enkla steg för att generera din QR-kod och börja samla in fler Google-recensioner redan idag
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {index < 2 && (
                <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-slate-200 -translate-y-1/2 z-0">
                  <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 rotate-[-90deg] w-5 h-5 text-slate-300" />
                </div>
              )}
              
              <Card className="h-full border-slate-200 hover:shadow-lg transition-shadow relative z-10 bg-white">
                <CardContent className="p-8 text-center">
                  <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <step.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-slate-200">
          {[
            { value: "60 sek", label: "Genomsnittlig tid" },
            { value: "100%", label: "Helt gratis" },
            { value: "0", label: "Registrering krävs" },
            { value: "A4", label: "Utskriftskvalitet" },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-blue-600 mb-1">{stat.value}</p>
              <p className="text-sm text-slate-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const scrollToHero = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section data-testid={TESTIMONIALS.container} className="py-20 px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium text-blue-700 bg-blue-50 rounded-full">
            Kundröster från Skåne
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Företagare som redan använder QR-kod-generatorn
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Över 500 lokala företag i Skåne har redan skapat sin QR-kod för recensioner. Läs vad de tycker!
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {TESTIMONIALS_DATA.map((testimonial) => (
            <Card
              key={testimonial.id}
              data-testid={`${TESTIMONIALS.card}-${testimonial.id}`}
              className="testimonial-card border-slate-200 hover:shadow-xl transition-all duration-300"
            >
              <CardContent className="p-8">
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                <blockquote className="text-slate-700 mb-6 leading-relaxed text-base">
                  &ldquo;{testimonial.quote}&rdquo;
                </blockquote>

                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-slate-100"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                    <p className="text-xs text-blue-600 font-medium">{testimonial.business}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12 p-8 bg-blue-50 rounded-2xl">
          <p className="text-lg text-slate-700 mb-4">
            <strong>Gör som Johan, Karin och Magnus</strong> – generera din egen QR-kod på 60 sekunder
          </p>
          <Button
            data-testid={TESTIMONIALS.microCta}
            onClick={scrollToHero}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 px-8"
          >
            <MousePointerClick className="w-5 h-5 mr-2" />
            Börja nu – det är gratis!
          </Button>
        </div>
      </div>
    </section>
  );
};

const renderFaqAnswer = (text) => {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const withBold = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const withBreaks = withBold.replace(/\n/g, "<br />");
  return { __html: withBreaks };
};

const FAQSection = () => (
  <section data-testid={FAQ.container} className="py-20 px-6 lg:px-8 bg-slate-50">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium text-blue-700 bg-blue-50 rounded-full">
          Vanliga frågor
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
          Allt du behöver veta om QR-kod och Google-recensioner
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Vi har samlat de vanligaste frågorna från företagare i Skåne om hur man får fler recensioner 
          och syns bättre på Google Maps.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-4">
        {FAQ_DATA.map((faq) => (
          <AccordionItem
            key={faq.id}
            value={faq.id}
            data-testid={`${FAQ.item}-${faq.id}`}
            className="bg-white border border-slate-200 rounded-xl px-6 data-[state=open]:shadow-lg transition-all"
          >
            <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-600 py-6 text-lg">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-slate-600 pb-6 leading-relaxed">
              <div
                className="[&_strong]:text-slate-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={renderFaqAnswer(faq.answer)}
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="text-center mt-12">
        <p className="text-slate-600 mb-4">
          Redo att generera din QR-kod och samla in fler Google-recensioner?
        </p>
        <Button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          variant="outline"
          className="border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold"
        >
          <FileText className="w-4 h-4 mr-2" />
          Skapa din QR-kod nu – 100% gratis
        </Button>
      </div>
    </div>
  </section>
);

const FooterSection = () => (
  <footer data-testid={FOOTER.container} className="bg-slate-900 py-20 px-6 lg:px-8">
    <div className="max-w-4xl mx-auto text-center">
      <div data-testid={FOOTER.statement} className="mb-12">
        <Building2 className="w-12 h-12 text-blue-400 mx-auto mb-6" />
        <h3 className="text-2xl font-bold text-white mb-4">
          Varför erbjuder vi detta gratis?
        </h3>
        <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mx-auto">
          Vi på Mellan Utveckling tror på att stärka det lokala näringslivet i Skåne. 
          Genom att hjälpa företag samla in fler recensioner bidrar vi till en starkare lokal ekonomi. 
          När ditt företag växer, växer hela Skåne!
        </p>
        <p className="text-slate-400 mt-6">
          Behöver ditt företag en ny, snabb hemsida eller en komplett strategi för Lokal SEO?
        </p>
      </div>

      <a
        href="https://mellanutveckling.se"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={FOOTER.ctaButton}
      >
        <Button className="h-14 px-10 text-lg font-semibold bg-blue-600 hover:bg-blue-500 text-white footer-cta shadow-xl">
          Läs mer om våra tjänster på mellanutveckling.se
          <ExternalLink className="w-5 h-5 ml-3" />
        </Button>
      </a>

      <div className="mt-16 pt-8 border-t border-slate-800">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400 mb-6">
          <a href="https://mellanutveckling.se/webbutveckling-skane/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            Webbutveckling Skåne
          </a>
          <span>•</span>
          <a href="https://mellanutveckling.se" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            Lokal SEO
          </a>
          <span>•</span>
          <a href="https://mellanutveckling.se/seo-skane/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            Seo Skåne
          </a>
          <span>•</span>
          <a href="https://mellanutveckling.se/kunskap/fler-google-recensioner-qr-kod/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            Hur får man fler recensioner?
          </a>
          <span>•</span>
          <a href="https://mellanutveckling.se/integritetspolicy/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
            Integritetspolicy
          </a>
        </div>
        
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} Mellan Utveckling AB. Alla rättigheter förbehållna. 
          <br className="sm:hidden" />
          <span className="hidden sm:inline"> | </span>
          Stärker lokala företag i Skåne sedan 2025.
        </p>
      </div>
    </div>
  </footer>
);

function App() {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const handleBusinessSelected = (business) => {
    setSelectedBusiness(business);
  };

  const handleLeadSubmit = (data) => {
    setLeadSubmitted(true);
  };

  return (
    <div className="App">
      <Header />
      
      <main>
        <div id="hero">
          <HeroSection
            onBusinessSelected={handleBusinessSelected}
            selectedBusiness={selectedBusiness}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>

        {selectedBusiness && (
          <div id="design-studio">
            <DesignStudio
              selectedBusiness={selectedBusiness}
              onSubmitLead={handleLeadSubmit}
            />
          </div>
        )}

        <HowItWorksSection />
        <TestimonialsSection />
        <FAQSection />
      </main>

      <FooterSection />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;